export const TIMELINE_STYLES = {
  line: "Linear Capsule · glowing time segments",
  trail: "Focus trail · textured curve",
  terrain: "Focus Terrain · luminous timing landscape",
  clock: "Session Clock · metrics within a luminous time ring",
  hourglass: "Hourglass Flow · tapered time slices",
  bars: "Focus bars · interval rhythm",
};

function segmentsWithPosition(segments) {
  const valid = (segments || []).filter((item) => Number.isFinite(item.seconds) && item.seconds > 0);
  const total = valid.reduce((sum, item) => sum + item.seconds, 0);
  let elapsed = 0;
  return total ? valid.map((item) => {
    const start = elapsed / total;
    elapsed += item.seconds;
    return { ...item, start, end: elapsed / total };
  }) : [];
}

function endpoint(ctx, x, y, color, radius, filled = false) {
  ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2);
  if (filled) { ctx.fillStyle = color; ctx.fill(); } else { ctx.strokeStyle = color; ctx.stroke(); }
}

function drawLine(ctx, box, intervals, focus, pause) {
  const { x, y, w, h, scale } = box, height = Math.min(h * .30, 58 * scale), top = y + (h - height) / 2;
  const reference = focus.toUpperCase() === "#FF7A1A";
  const focusGradient = ctx.createLinearGradient(0, top, 0, top + height);
  focusGradient.addColorStop(0, reference ? "#FFAF49" : focus);
  focusGradient.addColorStop(.43, focus);
  focusGradient.addColorStop(1, reference ? "#E95911" : `${focus}C8`);
  const pauseGradient = ctx.createLinearGradient(0, top, 0, top + height);
  pauseGradient.addColorStop(0, reference ? "#F5F6F8" : pause);
  pauseGradient.addColorStop(1, reference ? "#848E9B" : `${pause}B8`);
  // A low-opacity halo gives the capsule depth without a neon outline.
  intervals.forEach((item) => {
    if (item.kind !== "focus") return;
    ctx.save(); ctx.shadowColor = `${focus}60`; ctx.shadowBlur = 12 * scale;
    ctx.fillStyle = `${focus}12`;
    const width = (item.end - item.start) * w;
    ctx.beginPath(); ctx.roundRect(x + item.start * w, top, width, height, Math.min(16 * scale, width / 2)); ctx.fill(); ctx.restore();
  });
  ctx.save(); ctx.beginPath(); ctx.roundRect(x, top, w, height, height / 2); ctx.clip();
  ctx.fillStyle = "rgba(20,22,28,.64)"; ctx.fillRect(x, top, w, height);
  intervals.forEach((item) => {
    const left = x + item.start * w, width = (item.end - item.start) * w;
    ctx.fillStyle = item.kind === "focus" ? focusGradient : pauseGradient;
    ctx.fillRect(left, top, width, height);
    if (item.kind === "focus" && width > 16 * scale) {
      ctx.fillStyle = "rgba(255,255,255,.22)";
      ctx.fillRect(left + 4 * scale, top + 5 * scale, width - 8 * scale, 2 * scale);
    }
  });
  ctx.restore();
  ctx.strokeStyle = reference ? "rgba(231,234,241,.45)" : `${pause}90`;
  ctx.lineWidth = 2 * scale; ctx.beginPath(); ctx.roundRect(x, top, w, height, height / 2); ctx.stroke();
}

function drawBars(ctx, box, intervals, focus, pause, recorded) {
  const { x, y, w, h, scale } = box, center = y + h / 2;
  ctx.lineCap = "round";
  // Evenly spaced marks sample elapsed time; mark height distinguishes breaks.
  const count = Math.max(48, Math.floor(w / (9 * scale)));
  for (let index = 0; index < count; index++) {
    const fraction = (index + .5) / count;
    const item = intervals.find((entry) => fraction >= entry.start && fraction <= entry.end) || intervals[intervals.length - 1];
    const tall = item.kind === "focus", px = x + fraction * w;
    ctx.strokeStyle = tall ? focus : pause; ctx.lineWidth = 3 * scale;
    ctx.beginPath(); ctx.moveTo(px, center - (tall ? 24 : 8) * scale); ctx.lineTo(px, center + (tall ? 24 : 8) * scale); ctx.stroke();
  }
  if (recorded) { ctx.lineWidth = 2 * scale; endpoint(ctx, x, center, focus, 7 * scale); endpoint(ctx, x + w, center, focus, 7 * scale, true); }
}

// One vertical scale for the entire session. Each focus block becomes a hill
// whose height reflects its share of elapsed time, not an inferred attention
// level. Short breaks rest on the baseline; longer ones descend only after
// the first 4% of session time (a proportional grace interval).
export function buildTerrainProfile(segments) {
  const intervals = segmentsWithPosition(segments);
  const restLevel = .24;
  let level = restLevel;
  return intervals.map((item) => {
    const startLevel = level, span = item.end - item.start;
    const plateauEnd = item.kind === "break" ? Math.min(item.end, item.start + .04) : item.start;
    const peakLevel = item.kind === "focus" ? Math.min(.94, restLevel + 1.55 * span) : startLevel;
    level = item.kind === "focus" ? restLevel : Math.max(.08, level - 1.65 * (item.end - plateauEnd));
    return { ...item, startLevel, peakLevel, endLevel: level, plateauEnd };
  });
}

export function terrainLevelAt(item, fraction) {
  if (fraction <= item.start) return item.startLevel;
  if (fraction >= item.end) return item.endLevel;
  if (item.kind === "break" && fraction <= item.plateauEnd) return item.startLevel;
  if (item.kind === "focus") {
    const progress = (fraction - item.start) / Math.max(1e-9, item.end - item.start);
    const phase = progress < .5 ? progress * 2 : (1 - progress) * 2;
    const eased = phase * phase * (3 - 2 * phase);
    const floor = progress < .5 ? item.startLevel : item.endLevel;
    return floor + (item.peakLevel - floor) * eased;
  }
  const from = item.kind === "break" ? item.plateauEnd : item.start;
  const progress = (fraction - from) / Math.max(1e-9, item.end - from);
  const eased = progress * progress * (3 - 2 * progress);
  return item.startLevel + (item.endLevel - item.startLevel) * eased;
}

function drawTerrain(ctx, box, segments, focus, pause, recorded) {
  const { x, y, w, h, scale } = box, base = y + h * .90;
  const reference = focus.toUpperCase() === "#FF7A1A";
  if (!recorded) {
    // Historical totals have no ordered focus/break blocks. Show only their
    // proportional split, without manufacturing a climb or a dip.
    const split = segmentsWithPosition(segments), top = y + h * .60;
    split.forEach((item) => {
      const color = item.kind === "focus" ? focus : pause;
      ctx.fillStyle = `${color}25`; ctx.fillRect(x + item.start * w, top, (item.end - item.start) * w, base - top);
      ctx.strokeStyle = color; ctx.lineWidth = 3 * scale; ctx.beginPath();
      ctx.moveTo(x + item.start * w, top); ctx.lineTo(x + item.end * w, top); ctx.stroke();
    });
    return;
  }
  const intervals = buildTerrainProfile(segments);
  if (!intervals.length) return;
  const contour = (item, fraction) => y + h * (.85 - .72 * terrainLevelAt(item, fraction));
  ctx.lineCap = "round";
  intervals.forEach((item) => {
    const isFocus = item.kind === "focus", color = isFocus ? focus : pause;
    const samples = Math.max(3, Math.ceil((item.end - item.start) * 300));
    const curve = () => {
      ctx.beginPath();
      for (let index = 0; index <= samples; index++) {
        const fraction = item.start + (item.end - item.start) * index / samples;
        if (!index) ctx.moveTo(x + fraction * w, contour(item, fraction));
        else ctx.lineTo(x + fraction * w, contour(item, fraction));
      }
    };
    const fill = ctx.createLinearGradient(0, y + h * .12, 0, base);
    if (reference && isFocus) {
      fill.addColorStop(0, "#FFA039F0"); fill.addColorStop(.43, "#FF741AAC"); fill.addColorStop(1, "#C54A1217");
    } else if (reference) {
      fill.addColorStop(0, "#F0F2F6DB"); fill.addColorStop(.48, "#A6AFBD9C"); fill.addColorStop(1, "#6F788515");
    } else {
      fill.addColorStop(0, `${color}DB`); fill.addColorStop(.45, `${color}87`); fill.addColorStop(1, `${color}12`);
    }
    curve(); ctx.lineTo(x + item.end * w, base); ctx.lineTo(x + item.start * w, base); ctx.closePath();
    ctx.fillStyle = fill; ctx.fill();
    ctx.save(); ctx.shadowColor = `${color}55`; ctx.shadowBlur = (isFocus ? 10 : 5) * scale;
    ctx.strokeStyle = reference && isFocus ? "#FF9E38" : color;
    ctx.lineWidth = (isFocus ? 3.5 : 3) * scale; curve(); ctx.stroke(); ctx.restore();
  });
  // The luminous ridges still follow the fixed session-time scale, not attention.
}

function drawClock(ctx, box, intervals, focus, pause, recorded) {
  const { x, y, w, h, scale } = box, cx = x + w / 2, cy = y + h / 2;
  const radius = Math.min(h * .415, w * .25), start = -Math.PI / 2;
  const reference = focus.toUpperCase() === "#FF7A1A";
  const focusGradient = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
  focusGradient.addColorStop(0, reference ? "#FFAD43" : focus);
  focusGradient.addColorStop(.5, focus);
  focusGradient.addColorStop(1, reference ? "#E85A12" : focus);
  const pauseGradient = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
  pauseGradient.addColorStop(0, reference ? "#F7F8FA" : pause);
  pauseGradient.addColorStop(1, reference ? "#8B94A1" : pause);
  ctx.lineCap = "butt";
  // A quiet full ring keeps the clock silhouette intact behind every interval.
  ctx.strokeStyle = "rgba(28,30,35,.78)"; ctx.lineWidth = 19 * scale;
  ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.stroke();
  intervals.forEach((item) => {
    const isFocus = item.kind === "focus";
    ctx.save();
    ctx.strokeStyle = isFocus ? focusGradient : pauseGradient;
    ctx.lineWidth = 15 * scale;
    ctx.shadowColor = isFocus ? `${focus}66` : `${pause}35`;
    ctx.shadowBlur = (isFocus ? 11 : 5) * scale;
    ctx.beginPath(); ctx.arc(cx, cy, radius, start + item.start * Math.PI * 2, start + item.end * Math.PI * 2); ctx.stroke();
    ctx.restore();
  });
  // Fine inner and outer edges keep the ring crisp on transparent exports.
  ctx.strokeStyle = reference ? "rgba(255,255,255,.18)" : `${pause}38`;
  ctx.lineWidth = 1.2 * scale;
  [radius - 9 * scale, radius + 9 * scale].forEach((edge) => { ctx.beginPath(); ctx.arc(cx, cy, edge, 0, Math.PI * 2); ctx.stroke(); });
  for (let index = 0; index < 12; index++) {
    const angle = start + index * Math.PI / 6;
    const major = index % 3 === 0;
    ctx.strokeStyle = major ? `${focus}75` : "rgba(255,255,255,.22)";
    ctx.lineWidth = (major ? 2.2 : 1.3) * scale;
    ctx.beginPath(); ctx.moveTo(cx + Math.cos(angle) * (radius - (major ? 35 : 29) * scale), cy + Math.sin(angle) * (radius - (major ? 35 : 29) * scale));
    ctx.lineTo(cx + Math.cos(angle) * (radius - 22 * scale), cy + Math.sin(angle) * (radius - 22 * scale)); ctx.stroke();
  }
  if (recorded) {
    const firstColor = intervals[0].kind === "focus" ? focus : pause;
    const final = intervals[intervals.length - 1], finalAngle = start + final.end * Math.PI * 2;
    ctx.lineWidth = 2 * scale; endpoint(ctx, cx, cy - radius, firstColor, 6 * scale);
    endpoint(ctx, cx + Math.cos(finalAngle) * radius, cy + Math.sin(finalAngle) * radius, final.kind === "focus" ? focus : pause, 4 * scale, true);
  }
}

function drawHourglass(ctx, box, intervals, focus, pause) {
  const { x, y, w, h, scale } = box, cx = x + w / 2, cy = y + h / 2;
  const half = Math.min(w * .18, h * .69), high = h * .46, neck = 19 * scale;
  const top = cy - high, bottom = cy + high, count = 9, pitch = (bottom - top) / count, gap = 4 * scale;
  const reference = focus.toUpperCase() === "#FF7A1A";
  // Each pill is an area unit. Narrow neck slices contribute less time-area than
  // the wide chambers, so coloured intervals remain proportional to duration.
  const slices = Array.from({ length: count }, (_, index) => {
    const sliceTop = top + index * pitch, sliceHeight = pitch - gap;
    const halfWidth = neck + (half - neck) * Math.abs(sliceTop + sliceHeight / 2 - cy) / high;
    return { top: sliceTop, height: sliceHeight, halfWidth, area: 2 * halfWidth * sliceHeight };
  });
  const totalArea = slices.reduce((sum, slice) => sum + slice.area, 0);
  const focusFill = ctx.createLinearGradient(0, top, 0, bottom);
  if (reference) {
    focusFill.addColorStop(0, "#FFAC40"); focusFill.addColorStop(.48, "#F16414"); focusFill.addColorStop(1, "#FF8A25");
  } else { focusFill.addColorStop(0, focus); focusFill.addColorStop(1, `${focus}CE`); }
  const pauseFill = ctx.createLinearGradient(0, top, 0, bottom);
  if (reference) { pauseFill.addColorStop(0, "#F5F6F9"); pauseFill.addColorStop(.5, "#D9DEE7"); pauseFill.addColorStop(1, "#969EAA"); }
  else { pauseFill.addColorStop(0, pause); pauseFill.addColorStop(1, `${pause}C7`); }
  let passed = 0;
  slices.forEach((slice) => {
    const left = cx - slice.halfWidth, width = 2 * slice.halfWidth;
    const middle = (passed + slice.area / 2) / totalArea;
    const dominant = intervals.find((entry) => middle >= entry.start && middle < entry.end) || intervals[intervals.length - 1];
    ctx.save(); ctx.shadowColor = dominant.kind === "focus" ? `${focus}55` : `${pause}33`;
    ctx.shadowBlur = (dominant.kind === "focus" ? 10 : 5) * scale;
    ctx.fillStyle = dominant.kind === "focus" ? `${focus}14` : `${pause}12`;
    ctx.beginPath(); ctx.roundRect(left, slice.top, width, slice.height, Math.min(slice.height / 2, width / 2)); ctx.fill(); ctx.restore();
    ctx.save(); ctx.beginPath(); ctx.roundRect(left, slice.top, width, slice.height, Math.min(slice.height / 2, width / 2)); ctx.clip();
    // Split a slice into fine rows only when an interval boundary crosses it.
    // The pill silhouette and gloss stay intact; timing colour lives inside.
    const step = 1 * scale;
    for (let rowTop = slice.top; rowTop < slice.top + slice.height; rowTop += step) {
      const rowHeight = Math.min(step, slice.top + slice.height - rowTop);
      const rowArea = width * rowHeight, fraction = (passed + rowArea / 2) / totalArea;
      const item = intervals.find((entry) => fraction >= entry.start && fraction < entry.end) || intervals[intervals.length - 1];
      ctx.fillStyle = item.kind === "focus" ? focusFill : pauseFill;
      ctx.fillRect(left, rowTop, width, rowHeight + .25);
      passed += rowArea;
    }
    ctx.restore();
    ctx.strokeStyle = reference ? "rgba(255,255,255,.22)" : `${focus}45`;
    ctx.lineWidth = 1.4 * scale; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(left + 9 * scale, slice.top + 3.2 * scale);
    ctx.lineTo(left + width - 9 * scale, slice.top + 3.2 * scale); ctx.stroke();
  });
}

export function drawFocusTimeline(ctx, box, segments, style, focus, pause, recorded, drawTrail) {
  const intervals = segmentsWithPosition(segments);
  if (!intervals.length) return;
  ctx.save();
  if (style === "trail") drawTrail(ctx, box, segments, focus, pause, recorded);
  else if (style === "terrain") drawTerrain(ctx, box, segments, focus, pause, recorded);
  else if (style === "clock") drawClock(ctx, box, intervals, focus, pause, recorded);
  else if (style === "hourglass") drawHourglass(ctx, box, intervals, focus, pause, recorded);
  else if (style === "bars") drawBars(ctx, box, intervals, focus, pause, recorded);
  else drawLine(ctx, box, intervals, focus, pause, recorded);
  ctx.restore();
}
